import React from 'react';
import { Button } from '../ui/button';
import { Monitor, Zap, PlayCircle, ArrowRight, Globe, Layout, Smartphone } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import SEO from '../SEO';

const CrossBrowserTesting = ({ onBack, onLogin, onRegister }) => {
  return (
    <PageLayout onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Cross-Browser Testing - Test Across All Browsers | Lexa.run"
        description="Ensure your application works perfectly across all browsers and devices. Automated cross-browser testing with AI."
      />

      <main className="container mx-auto px-4 sm:px-6 py-12 pt-24">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Monitor className="h-4 w-4 mr-2" />
            Cross-Browser Testing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Test Across All Browsers
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Ensure pixel-perfect consistency across Chrome, Firefox, Safari, Edge, and mobile browsers. Automated testing on real browsers with AI-powered validation.
          </p>
          <Button onClick={onRegister} size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white">
            <PlayCircle className="h-5 w-5 mr-2" />
            Start Testing Now
          </Button>
        </div>

        {/* Key Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">What You Can Test</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Browser Compatibility</h3>
              <p className="text-gray-600">Test your application across Chrome, Firefox, Safari, Edge, and Opera automatically.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Layout className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Layout Consistency</h3>
              <p className="text-gray-600">Verify that your layouts render correctly across different browser engines and versions.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">JavaScript Compatibility</h3>
              <p className="text-gray-600">Test JavaScript functionality across different browser implementations and ES versions.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <Smartphone className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Mobile Browsers</h3>
              <p className="text-gray-600">Test on mobile Chrome, Safari, Samsung Internet, and other mobile browsers.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                <Monitor className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Legacy Support</h3>
              <p className="text-gray-600">Validate compatibility with older browser versions for enterprise environments.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">CSS & Media Queries</h3>
              <p className="text-gray-600">Ensure CSS features, animations, and media queries work across all browsers.</p>
            </div>
          </div>
        </div>

        {/* Supported Browsers */}
        <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-2xl p-12 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Supported Browsers</h2>
          <p className="text-center text-gray-600 mb-8">Test on all major desktop and mobile browsers</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="flex items-center justify-center mb-3">
                <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/x5knzx5x_image.png" alt="Chrome" className="h-16 w-16 object-contain" />
              </div>
              <h4 className="font-bold">Chrome</h4>
              <p className="text-sm text-gray-600">Desktop & Mobile</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="flex items-center justify-center mb-3">
                <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/6h09ca1f_image.png" alt="Firefox" className="h-16 w-16 object-contain" />
              </div>
              <h4 className="font-bold">Firefox</h4>
              <p className="text-sm text-gray-600">All versions</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="flex items-center justify-center mb-3">
                <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/8ggetaxk_image.png" alt="Safari" className="h-16 w-16 object-contain" />
              </div>
              <h4 className="font-bold">Safari</h4>
              <p className="text-sm text-gray-600">macOS & iOS</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="flex items-center justify-center mb-3">
                <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/uc6s1622_image.png" alt="Edge" className="h-16 w-16 object-contain" />
              </div>
              <h4 className="font-bold">Edge</h4>
              <p className="text-sm text-gray-600">Chromium-based</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="flex items-center justify-center mb-3">
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="iOS" className="h-16 w-16 object-contain" />
              </div>
              <h4 className="font-bold">iOS</h4>
              <p className="text-sm text-gray-600">iPhone & iPad</p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Select Browsers</h3>
              <p className="text-gray-600">Choose which browsers and versions you want to test on from our comprehensive matrix.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Run Tests</h3>
              <p className="text-gray-600">Execute your test suite in parallel across all selected browsers for faster results.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Compare Results</h3>
              <p className="text-gray-600">Get detailed comparison reports with screenshots, logs, and browser-specific issues highlighted.</p>
            </div>
          </div>
        </div>

        {/* Integrations Section - New Design */}
        <div className="mb-16 bg-white rounded-2xl p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fits into your existing workflow
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                With comprehensive integrations for <span className="text-blue-600 font-semibold">CI/CD</span>, <span className="text-blue-600 font-semibold">issue tracking</span>, and <span className="text-blue-600 font-semibold">test case management</span>, Lexa fits into your current development and testing process with minimal disruption.
              </p>
            </div>

            {/* Right Column - Integration Logos */}
            <div>
              <div className="grid grid-cols-3 gap-4">
                {/* Row 1 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/poehpjw0_image.png" alt="Azure DevOps" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/3/32/Atlassian_Bitbucket_Logo.png" alt="Bitbucket" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/tdtq4iim_image.png" alt="CircleCI" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 2 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/9/91/Octicons-mark-github.svg" alt="GitHub" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/lbn2nq9z_image.png" alt="Discord" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/GitLab_logo.svg" alt="GitLab" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 3 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/r25r80p5_image.png" alt="Jenkins" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/hymy3wvl_image.png" alt="TeamCity" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/mc6i5gez_image.png" alt="Jira" className="h-12 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Test Across All Browsers?</h2>
          <p className="text-xl mb-8 opacity-90">Start testing in minutes, not days. No credit card required.</p>
          <Button onClick={onRegister} size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
            Start Free Trial
          </Button>
        </div>
      </main>
    </PageLayout>
  );
};

export default CrossBrowserTesting;
